import { Reveal } from "./Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight, Fingerprint } from "lucide-react";

const AWG_IMG =
  "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHx3YXRlciUyMGNvbmRlbnNhdGlvbiUyMGVuZ2luZWVyaW5nJTIwaGFyZHdhcmV8ZW58MHx8fHwxNzkyOTk3ODE0fDA&ixlib=rb-4.1.0&q=85";

const Tag = ({ children }) => (
  <span className="text-[11px] uppercase tracking-[0.12em] text-[#A1A1AA] border border-white/15 rounded-full px-3 py-1">
    {children}
  </span>
);

const PlaceholderLink = ({ label, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1.5 text-sm text-[#71717A] cursor-default"
    title="Coming soon"
  >
    {label}
    <span className="text-[10px] uppercase tracking-[0.12em] border border-white/10 rounded-full px-2 py-0.5 text-[#71717A]">
      Soon
    </span>
  </span>
);

export const Projects = () => (
  <section id="projects" data-testid="projects-section" className="py-24 md:py-32 lg:py-40 border-t border-white/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-[#71717A] font-mono-alt">Featured Projects</p>
        <h2 className="font-heading text-4xl md:text-5xl tracking-tight leading-tight font-medium text-[#F9F9F9] mt-4">
          Things I’m building.
        </h2>
        <p className="text-base md:text-lg text-[#A1A1AA] mt-6 max-w-xl leading-relaxed">
          Projects driven by curiosity, experimentation, and the desire to solve real problems.
        </p>
      </Reveal>

      {/* PROJECT 01 — BIOPAY */}
      <article data-testid="project-biopay" className="mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-6 lg:sticky lg:top-32">
          <Reveal>
            <p className="font-heading text-7xl md:text-8xl font-medium text-white/10 leading-none">01</p>
            <p className="text-xs uppercase tracking-[0.25em] text-[#0EA5E9] font-mono-alt mt-6">
              Fintech · Startup · Biometric Payments
            </p>
            <h3 className="font-heading text-3xl md:text-4xl tracking-tight font-medium text-[#F9F9F9] mt-4">BioPay</h3>
            <p className="font-heading text-xl md:text-2xl text-[#A1A1AA] mt-2">Exploring a more seamless way to pay.</p>
            <p className="text-sm md:text-base text-[#71717A] leading-relaxed mt-6 max-w-lg">
              BioPay is a fintech concept exploring how biometric authentication could simplify
              payments and digital wallets, starting with campus environments.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Making everyday payments more convenient",
                "Biometric authentication for payment experiences",
                "Campus infrastructure meeting digital wallets",
                "Product feasibility and the development journey",
                "A practical, user-friendly payment experience",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                  <span className="mt-2 h-px w-4 bg-[#0EA5E9] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 mt-8">
              {["Biometric Authentication", "Digital Wallet", "Payments", "Product Design"].map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </div>
            <p className="mt-6 text-[11px] uppercase tracking-[0.15em] text-[#71717A] font-mono-alt">
              Status: Concept — working toward an MVP
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6">
              <PlaceholderLink testid="biopay-screenshots-link" label="Product screenshots" />
              <PlaceholderLink testid="biopay-demo-link" label="MVP demonstration" />
              <PlaceholderLink testid="biopay-repo-link" label="GitHub repository" />
              <Link
                data-testid="biopay-case-study-link"
                to="/case-study/biopay"
                className="inline-flex items-center gap-1.5 text-sm text-[#F9F9F9] hover:text-[#0EA5E9] transition-colors"
              >
                Case study <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 relative">
          <Reveal delay={0.15}>
            {/* Concept interface mockup — placeholder, no real product data */}
            <div
              data-testid="biopay-concept-mockup"
              className="relative rounded-sm border border-white/10 bg-[#0C0C0C] p-6 md:p-10 overflow-hidden"
            >
              <div
                className="absolute -top-24 -right-24 h-64 w-64 rounded-full border border-[#0EA5E9]/20"
                aria-hidden="true"
              />
              <div
                className="absolute -top-12 -right-12 h-40 w-40 rounded-full border border-white/5"
                aria-hidden="true"
              />
              <div className="flex items-center justify-between">
                <span className="font-heading text-lg font-medium text-[#F9F9F9]">BioPay</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#0EA5E9] border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 rounded-full px-3 py-1">
                  Concept
                </span>
              </div>

              {/* Wallet card */}
              <div className="mt-8 rounded-lg border border-white/10 bg-[#111111] p-6 relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#0EA5E9]" aria-hidden="true" />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Campus Wallet</span>
                  <Fingerprint size={20} className="text-[#0EA5E9]" />
                </div>
                <p className="font-mono-alt text-sm text-[#A1A1AA] mt-6 tracking-[0.3em]">•••• •••• ••••</p>
                <div className="flex items-end justify-between mt-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">Balance</p>
                    <p className="font-heading text-2xl font-medium text-[#F9F9F9] mt-1 tracking-widest">• • •</p>
                  </div>
                  <span className="h-2 w-2 rounded-full bg-[#0EA5E9]" />
                </div>
              </div>

              {/* Biometric auth row */}
              <div className="mt-6 flex items-center gap-4 rounded-lg border border-white/10 bg-[#111111] p-5">
                <div className="relative flex items-center justify-center h-12 w-12 rounded-full border border-[#0EA5E9]/40">
                  <div className="absolute inset-0 rounded-full border border-[#0EA5E9]/20 scale-125" aria-hidden="true" />
                  <Fingerprint size={20} className="text-[#0EA5E9]" />
                </div>
                <div>
                  <p className="text-sm text-[#F9F9F9]">Authenticate to pay</p>
                  <p className="text-xs text-[#71717A] mt-1">Biometric verification — concept flow</p>
                </div>
              </div>

              {/* Skeleton transaction rows */}
              <div className="mt-6 space-y-3">
                {[0, 1].map((i) => (
                  <div key={i} className="flex items-center gap-4 rounded-lg border border-white/5 p-4">
                    <span className="h-8 w-8 rounded-full bg-white/5 shrink-0" aria-hidden="true" />
                    <div className="flex-1 space-y-2">
                      <span className="block h-2 w-24 rounded bg-white/10" aria-hidden="true" />
                      <span className="block h-2 w-16 rounded bg-white/5" aria-hidden="true" />
                    </div>
                    <span className="h-2 w-10 rounded bg-[#0EA5E9]/20" aria-hidden="true" />
                  </div>
                ))}
              </div>

              <p className="mt-8 text-[10px] uppercase tracking-[0.2em] text-[#71717A] font-mono-alt">
                Placeholder mockup — product screenshots coming soon
              </p>
            </div>
          </Reveal>
        </div>
      </article>

      {/* PROJECT 02 — ATMOSPHERIC WATER GENERATOR */}
      <article data-testid="project-awg" className="mt-32 md:mt-48 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-6 order-2 lg:order-1 relative">
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-sm border border-white/10 group">
              <img
                src={AWG_IMG}
                alt="Industrial metal and pipework — engineering texture for the Atmospheric Water Generator"
                className="w-full h-[420px] md:h-[560px] object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-transparent" />
              {/* Schematic annotation */}
              <div className="absolute top-6 left-6 md:left-8 bg-[#080808]/80 backdrop-blur-md border border-white/10 rounded-lg p-5 max-w-[240px]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#0EA5E9] font-mono-alt">Working principle</p>
                <ol className="mt-3 space-y-2.5">
                  {[
                    "Humid air passes over a cooled surface",
                    "A Peltier module cools it below the dew point",
                    "Water vapor condenses into droplets",
                    "Condensate is collected below",
                  ].map((s, i) => (
                    <li key={i} className="flex gap-2.5 text-xs text-[#A1A1AA] leading-snug">
                      <span className="font-mono-alt text-[#0EA5E9] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <Reveal>
            <p className="font-heading text-7xl md:text-8xl font-medium text-white/10 leading-none">02</p>
            <p className="text-xs uppercase tracking-[0.25em] text-[#0EA5E9] font-mono-alt mt-6">
              Hardware · Engineering · Sustainability
            </p>
            <h3 className="font-heading text-3xl md:text-4xl tracking-tight font-medium text-[#F9F9F9] mt-4">
              Atmospheric Water Generator
            </h3>
            <p className="font-heading text-xl md:text-2xl text-[#A1A1AA] mt-2">
              Exploring how water can be collected from the air.
            </p>
            <p className="text-sm md:text-base text-[#71717A] leading-relaxed mt-6 max-w-lg">
              An experimental engineering project exploring atmospheric water collection through
              condensation. The device investigates how cooling technology and heat transfer can be
              used to condense moisture from humid air and collect the resulting water.
            </p>
            <p className="text-sm md:text-base text-[#71717A] leading-relaxed mt-4 max-w-lg">
              A Peltier thermoelectric module creates a temperature difference that can cool a
              surface below the air’s dew point, allowing water vapor to condense on it.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Thermoelectric cooling",
                "Heat dissipation and thermal management",
                "Atmospheric humidity and condensation",
                "Water collection and system design",
                "Energy consumption and cooling efficiency",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                  <span className="mt-2 h-px w-4 bg-[#0EA5E9] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 mt-8">
              {["Peltier Module", "Thermoelectric Cooling", "Heat Transfer", "Atmospheric Water Collection", "Experimental Engineering"].map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </div>
            <div className="mt-10 border border-white/10 rounded-lg p-6 bg-[#111111]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] font-mono-alt">Case study outline</p>
              <ol className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {[
                  "Problem and motivation",
                  "Working principle",
                  "Components and materials",
                  "Construction and experimentation",
                  "Challenges encountered",
                  "Measurements and results",
                  "Future improvements",
                ].map((s, i) => (
                  <li key={i} className="flex gap-2.5 text-xs text-[#A1A1AA]">
                    <span className="font-mono-alt text-[#71717A] shrink-0">{i + 1}.</span>
                    {s}
                  </li>
                ))}
              </ol>
              <Link
                data-testid="awg-case-study-link"
                to="/case-study/atmospheric-water-generator"
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#F9F9F9] hover:text-[#0EA5E9] transition-colors"
              >
                Read the case study <ArrowUpRight size={13} />
              </Link>
            </div>
          </Reveal>
        </div>
      </article>
    </div>
  </section>
);
